// src/components/BoardView.tsx
import React, { useState } from 'react';
import { FileEdit, Cog, Eye, Ban, CheckCircle2 } from 'lucide-react';
import { tasksApi, Task } from '../api/tasks';
import TaskCard from './TaskCard';
import { usePermissionGuard } from '../hooks/usePermissionGuard';

interface BoardViewProps {
  tasks: Task[];
  onRefresh: () => void;
}

const statusColumns = [
  { status: 'TODO',        label: 'To Do',       icon: <FileEdit className="w-3.5 h-3.5" />,    tile: '' },
  { status: 'IN_PROGRESS', label: 'In Progress', icon: <Cog className="w-3.5 h-3.5" />,         tile: '' },
  { status: 'IN_REVIEW',   label: 'In Review',   icon: <Eye className="w-3.5 h-3.5" />,         tile: 'is-warm' },
  { status: 'BLOCKED',     label: 'Blocked',     icon: <Ban className="w-3.5 h-3.5" />,         tile: 'is-alert' },
  { status: 'DONE',        label: 'Done',        icon: <CheckCircle2 className="w-3.5 h-3.5" />,tile: '' },
];

export function BoardView({ tasks, onRefresh }: BoardViewProps) {
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { showError, showSuccess } = usePermissionGuard();

  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    setDraggedTaskId(taskId);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
  };

  const handleDragEnd = () => {
    setDraggedTaskId(null);
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('text/plain');
    
    if (!taskId) return;
    
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    
    if (task.status === newStatus) {
      setIsDragging(false);
      return;
    }

    try {
      await tasksApi.changeStatus(taskId, { status: newStatus });
      showSuccess('Task Moved', `Task moved to ${statusColumns.find(c => c.status === newStatus)?.label}`);
      onRefresh();
    } catch (err: any) {
      showError('MOVE_FAILED', err.message || 'Failed to move task');
    } finally {
      setIsDragging(false);
      setDraggedTaskId(null);
    }
  };

  const getColumnTasks = (status: string) => {
    return tasks.filter(task => task.status === status);
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-4 min-h-[500px] gt-scroll">
      {statusColumns.map((column, i) => {
        const columnTasks = getColumnTasks(column.status);

        return (
          <div
            key={column.status}
            className="gt-column flex-shrink-0 w-80 flex flex-col"
            style={{ minHeight: '400px' }}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, column.status)}
          >
            <div className="gt-column-head">
              <div className={`gt-icon-tile ${column.tile}`}>
                {column.icon}
              </div>
              <div className="flex flex-col">
                <span className="gt-eyebrow">{['i.','ii.','iii.','iv.','v.'][i]}</span>
                <span className="gt-display" style={{ fontSize: 14, color: 'var(--gt-text-primary)', lineHeight: 1, marginTop: 1 }}>{column.label}</span>
              </div>
              <span className="gt-count gt-numerals">{String(columnTasks.length).padStart(2, '0')}</span>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto max-h-[calc(100vh-300px)] p-3 gt-scroll">
              {columnTasks.map((task) => (
                <div
                  key={task.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, task.id)}
                  onDragEnd={handleDragEnd}
                  className={`cursor-grab active:cursor-grabbing transition-all duration-200 ${
                    draggedTaskId === task.id ? 'opacity-50 scale-95' : ''
                  }`}
                >
                  <TaskCard task={task} onRefresh={onRefresh} />
                </div>
              ))}

              {columnTasks.length === 0 && (
                <div className="gt-empty" style={{ padding: '32px 16px', border: '1px dashed var(--gt-border-strong)', borderRadius: 14 }}>
                  <span className="gt-page-sub">Drop tasks <em className="gt-serif-italic">here</em></span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}